import { logger } from './logger';

/**
 * @desc Light behaviour for injected insights fragments (D1: fragments stay un-hydrated). A
 * fragment marks an element with `data-enhance="<name>"` (space-separated for several) and this
 * module wires the named behaviour onto it with plain DOM listeners — no React, no state beyond the
 * DOM. Native HTML does the work wherever it can; an enhancer only fills the gap.
 *
 * Every listener is bound to the caller's `signal`, so aborting it (the loader's cleanup) removes
 * them all. Anything stateful enough to outgrow this belongs in the SPA, rendered from a data
 * fragment's JSON — not in a bigger enhancer.
 */
export type Enhancer = (element: HTMLElement, signal: AbortSignal) => void;

/**
 * @desc `details-group`: the `<details>` inside act as one accordion — opening one closes the
 * others. Current browsers already do this for `<details name>`; this covers those that do not.
 * `toggle` does not bubble, so it is caught in the capture phase.
 */
const detailsGroup: Enhancer = (element, signal) => {
  element.addEventListener(
    'toggle',
    (event) => {
      const opened = event.target;
      if (!(opened instanceof HTMLDetailsElement) || !opened.open) return;
      for (const details of element.querySelectorAll('details')) {
        if (details !== opened && details.open) details.open = false;
      }
    },
    { capture: true, signal },
  );
};

/**
 * @desc `radio-panels`: each `input[type=radio][data-panel-target]` shows the `[data-panel]` whose
 * value matches its target and hides the rest. Synced once on wiring, so the panels follow
 * whichever radio the server checked.
 */
const radioPanels: Enhancer = (element, signal) => {
  const sync = () => {
    const checked = element.querySelector<HTMLInputElement>(
      'input[type="radio"][data-panel-target]:checked',
    );
    const target = checked?.dataset.panelTarget;
    if (target === undefined) return;
    for (const panel of element.querySelectorAll<HTMLElement>('[data-panel]')) {
      panel.hidden = panel.dataset.panel !== target;
    }
  };
  element.addEventListener('change', sync, { signal });
  sync();
};

export const fragmentEnhancers: Readonly<Record<string, Enhancer>> = {
  'details-group': detailsGroup,
  'radio-panels': radioPanels,
};

/** @desc Wires every `[data-enhance]` element under `root`; an unknown name is logged and skipped. */
export function enhanceFragment(
  root: HTMLElement,
  signal: AbortSignal,
  registry: Readonly<Record<string, Enhancer>> = fragmentEnhancers,
): void {
  for (const element of root.querySelectorAll<HTMLElement>('[data-enhance]')) {
    for (const name of (element.dataset.enhance ?? '').split(/\s+/).filter(Boolean)) {
      const enhancer = registry[name];
      if (!enhancer) {
        logger.warn('Unknown fragment enhancer — the fragment is newer than this portal', {
          enhancer: name,
        });
        continue;
      }
      enhancer(element, signal);
    }
  }
}
